
import React, { useEffect, useState, useContext, useMemo, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { UserAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import ProfileContext from '../context/ProfileContext';
import moment from 'moment';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { useUserContext } from '../context/UserContext';
import { Button, Modal } from 'react-bootstrap';
import { useStepContext } from '../context/StepContext';
import { isRevalidationRequired } from '../utils/revalidation';
import RevalidationPromptModal from '../components/RevalidationPromptModal';

const Account = () => {
  const { userData, handleUserChange } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = UserAuth();
  const { profile, setProfile } = useContext(ProfileContext);
  const { setIsCompleted, setCurrentStep, setInterviewData } = useStepContext();

  const [isVerified, setVerification] = useState(false);
  const [isOtherVerified, setOtherVerified] = useState(false);
  const [isEducationVerified, setEducationVerified] = useState(false);
  const [isHeadlineVerified, setHeadlineVerified] = useState(false);
  const [isAppDownloaded, setIsAppDownloaded] = useState(false);
  const [isInterviewCompleted, setIsInterviewCompleted] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0)
  const [interviewStatus, setInterviewStatus] = useState(null)
  const [interviewDateTime, setInterviewDateTime] = useState(null)
  const [checkProfile] = useState(0)
  const [pageLoading, setPageLoading] = useState(true)
  const [candidateList, setCandidateList] = useState([])
  const [selectedCandidateIndex, setSelectedCandidateIndex] = useState(0)
  const [selectionLoading, setSelectionLoading] = useState(false)
  const hasInitialized = useRef(false);
  const lastUid = useRef(null);
  const toastShown = useRef(false);
  const isSchedulingRef = useRef(false);
  const sentryUserEmailRef = useRef('Unknown Email');

  useEffect(() => {
    if (user?.email) {
      sentryUserEmailRef.current = user.email;
      try {
        Sentry.setUser({ email: user.email });
      } catch (_) { /* Sentry unavailable */ }
    }
  }, [user?.email]);

  const logFieldToSentry = (fieldName, value) => {
    try {
      Sentry.captureMessage(`User filled field: ${fieldName} (User: ${sentryUserEmailRef.current?.replace('@', ' at ')})`, {
        level: 'info',
        extra: { value, email: sentryUserEmailRef.current }
      });
    } catch (_) { /* Sentry unavailable */ }
  };

  const logSentryException = (error, extra = {}) => {
    try {
      Sentry.captureException(error, { extra: { email: user?.email, ...extra } });
    } catch (_) { /* Sentry unavailable */ }
  };

  useEffect(() => {
    const handleFocusOut = (e) => {
      if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA')) {
        const fieldName = e.target.name || e.target.placeholder || e.target.id || 'unknown_field';
        const isOtpField = String(e.target.name || '').startsWith('ssn-') || /otp/i.test(String(fieldName)) || /otp/i.test(String(e.target.id || ''));
        const isFileInput = e.target.type === 'file';
        let logValue = isOtpField ? '[REDACTED]' : e.target.value;
        if (isFileInput && e.target.files?.[0]) {
          logValue = { fileName: e.target.files[0].name, fileSize: e.target.files[0].size };
        }
        logFieldToSentry(fieldName, logValue);
      }
    };
    document.addEventListener('focusout', handleFocusOut);
    return () => {
      document.removeEventListener('focusout', handleFocusOut);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reschedule Modal State
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleHour, setRescheduleHour] = useState(12);
  const [rescheduleMinute, setRescheduleMinute] = useState(0);
  const [rescheduleAmPm, setRescheduleAmPm] = useState('AM');
  const [rescheduleAcknowledged, setRescheduleAcknowledged] = useState(false);
  const [showRevalidationModal, setShowRevalidationModal] = useState(false);
  // Auto-reset acknowledgement if date or time changes
  useEffect(() => {
    setRescheduleAcknowledged(false);
  }, [rescheduleDate, rescheduleHour, rescheduleMinute, rescheduleAmPm]);



  const handleDownloadClick = () => {
    logFieldToSentry('Download app click', true);
    navigate('/download');
  }

  const completedStep = useMemo(() => {
    if (!isVerified) return 1;

    if (!isOtherVerified || !isEducationVerified || !isHeadlineVerified)
      return 2;

    if (!isAppDownloaded)
      return 3;

    if (isInterviewCompleted)
      return 5;

    // Profile fully complete and app downloaded → Activate Take Interview (Step 4)
    return 4;

  }, [isVerified, isOtherVerified, isEducationVerified, isHeadlineVerified, isAppDownloaded, isInterviewCompleted]);

  // ✅ All 4 verified = profile is fully complete
  const isProfileComplete = isVerified && isOtherVerified && isEducationVerified && isHeadlineVerified;


  const getIntDate = useCallback(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }, []);

  const checkAlreadyExist = useCallback(async (reqId) => {
    if (!reqId) return 0;
    try {
      const refreshedToken = user?.getIdToken ? await user.getIdToken() : null;
      if (!refreshedToken) return 0;
      const data = JSON.stringify({
        "operation": "checkcandidate",
        "checkby": "email",
        "email": user?.email,
        "requisition_id": reqId,
      });
      const headers = {
        'Authorization': `Bearer ${refreshedToken}`,
        'Content-Type': 'application/json'
      };
      const response = await axios.post(
        `${process.env.REACT_APP_UPLOAD}/api/v3.0/candidates/check-candidate`,
        data,
        { headers }
      );
      return response.data?.isExist === 1 ? 1 : 0;
    } catch (error) {
      logSentryException(error);
      return 0;
    }
  }, [user]);

  const addCandidateStatus = useCallback(async (reqId) => {
    if (!reqId) return;
    try {
      const refreshedToken = user?.getIdToken ? await user.getIdToken() : null;
      if (!refreshedToken) return;
      let postData = {
        requisitionId: reqId,
        name: user?.displayName,
        email: user?.email,
        type: "candidate",
        status: "Candidate Added",
        note: `${user?.displayName} has been added by Sheela AI on ${moment().format('YYYY-MM-DD')} for the requisition.`,
        addedBy: "Sheela AI",
        addedByEmail: "sheela@virtualemployee.com",
        userType: "1"
      };
      const headers = {
        'Authorization': `Bearer ${refreshedToken}`,
        'Content-Type': 'application/json'
      };
      const response = await axios.post(
        `${process.env.REACT_APP_UPLOAD}/api/v3.0/requisitions/add-status`,
        postData,
        { headers }
      );
      return response;
    } catch (error) {
      logSentryException(error);
      console.error("Error adding candidate status:", error);
      throw error;
    }
  }, [user]);

  const interviewSchedule = useCallback(async (getUserdata, reqId, interviewType) => {
    if (!reqId || isSchedulingRef.current) return;
    isSchedulingRef.current = true;

    const now = new Date();
    const gettime = now.toTimeString().slice(0, 8);
    const getInterviewdate = getIntDate();
    const postData = {
      user_name: user?.displayName,
      email: user?.email,
      added_by_email: "sheela@virtualemployee.com",
      added_by_name: "Sheela AI",
      added_by_id: "",
      hrType: "1",
      contact_no: getUserdata?.contact_no?.toString().replace("+", ""),
      operation: "addcandidate",
      resume_url: getUserdata?.resume_url,
      request_id: reqId,
      requisition_id: reqId,
      referralCheck: 0,
      is_referral: 0,
      experience: getUserdata?.total_exp,
      current_ctc: getUserdata?.annual_salary,
      technical_round: interviewType === 'technical' ? 1 : 0,
      one2one_round: (interviewType === 'non-technical' || interviewType === 'technical') ? 1 : 0,
      comm_skill_rating: '',
      cultural_fit_rating: '',
      duration: '',
      audio_url: '',
      relevant_experience: '',
      relevant_exp: '',
      relavant_experience: '',
      expected_ctc: '',
      interview_date: getInterviewdate,
      interview_time: gettime,
      notice_period: getUserdata?.notice_period || "",
      current_company: getUserdata?.current_company || "",
      source_of_candidate: new URLSearchParams(location.search).get('source') || userData?.source_of_candidate || getUserdata?.source_of_candidate || "company_website",
      interview_medium: "NM Interview",
      job_status: getUserdata?.job_status || "",
    };
    const refreshedToken = await user?.getIdToken();
    const headers = { 'Authorization': `Bearer ${refreshedToken}` };
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_UPLOAD}/api/v3.0/candidates/add-candidate`,
        postData,
        { headers }
      );
      if (response.data?.statusCode === 200) {
        await addCandidateStatus(reqId);
        getScheduledata();

        // Automatically trigger Sheela questions if candidate came from URL
        if (userData?.getReqId) {
          try {
            const candidateUrl = `${process.env.REACT_APP_CANDIDATE}/getCandidateDetails`;
            const candidateRes = await axios.post(candidateUrl, { email: user?.email }, { headers });
            const candidateData = candidateRes.data?.candidate?.[0];

            if (candidateData && candidateData?.requisitionDetails?.requisition_for === "avp") {

              // Check if Sheela round questions already exist for this candidate + requisition
              const checkUrl = `${process.env.REACT_APP_OPEN_API}/check-sheela-round-questions`;
              const checkRes = await axios.post(checkUrl, {
                requisition_id: candidateData?.requisition_id,
                user_id: userData?.userId,
              }, { headers });

              if (checkRes.data?.exists) {
                // Questions already generated — skip
                return;
              }

              const questionUrl = `${process.env.REACT_APP_OPEN_API}/getSheelaRoundQuestions`;
              await axios.post(questionUrl, {
                "user_id": userData?.userId,
                "req_id": candidateData?.requisition_id,
                "userexperience": candidateData?.totalExp
              }, { headers });
            }
          } catch (err) {
            logSentryException(err);
            console.error("Error generating Sheela questions:", err);
          }
        }

        // Show success toast for existing users
        if (!toastShown.current) {
          const intDate = getInterviewdate ? moment(getInterviewdate).format('DD-MM-YYYY') : '';
          toast.success(`You are now registered for the VEREQ ID ${reqId}, and your interview is scheduled for ${intDate}.`);
          toastShown.current = true;
          // Clear location state to prevent toast on refresh if it was there
          navigate(location.pathname, { replace: true, state: {} });
        }
      } else if (response.data?.statusCode === 429) {
        toast.error(response.data?.message || 'Too many interviews already scheduled. Cannot schedule more than 3 interviews within a one month period.');
      }
    } catch (error) {
      logSentryException(error);
      if (error.response?.status === 429) {
        toast.error(error.response?.data?.message || 'Too many interviews already scheduled. Cannot schedule more than 3 interviews within a one month period.');
      } else {
        console.error("Error scheduling interview:", error);
      }
    } finally {
      isSchedulingRef.current = false;
    }
  }, [user, addCandidateStatus, getIntDate]);

  const handleCandidateSelection = useCallback((index, candidates, isInitialLoad = false) => {
    if (!candidates || candidates.length === 0) return;

    // Show local loader for switching
    if (!isInitialLoad) {
      setSelectionLoading(true);
    }

    const candidate = candidates[index];
    setSelectedCandidateIndex(index);
    logFieldToSentry('Candidate selection', { index, requisitionId: candidate?.requisition_id });

    // Update context only if data is different to avoid unnecessary triggers
    handleUserChange("getJobtitle", candidate?.requisitionDetails?.jobTitle || "");
    handleUserChange("getReqIdValue", candidate?.requisition_id || "");

    if (candidate.source === 'previous') {
      // Robust Date Parsing
      const rawDate = candidate.interview_date || candidate.test_date || candidate.created_at || candidate.submitted_at;
      const rawTime = candidate.interview_time || "00:00:00";

      let interviewDateTimeIST;

      if (rawDate && String(rawDate).includes('T')) {
        interviewDateTimeIST = moment(rawDate).toDate();
      } else if (rawDate) {
        const datePart = String(rawDate).split(' ')[0];
        const timePart = String(rawTime).split('.')[0];
        interviewDateTimeIST = moment(`${datePart} ${timePart}`, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", "YYYY-MM-DD", "DD-MM-YYYY"]).toDate();
      } else {
        interviewDateTimeIST = new Date();
      }

      setInterviewStatus('completed')
      setTimerSeconds(0)
      setInterviewDateTime(interviewDateTimeIST)
    } else {
      const { status, remainingSeconds, interviewDateTimeIST } = isInterviewWithin48Hours(
        candidate?.interview_date,
        candidate?.interview_time
      )
      setInterviewStatus(status)
      setTimerSeconds(remainingSeconds)
      setInterviewDateTime(interviewDateTimeIST)
    }

    // Hide loader after a short delay
    setTimeout(() => {
      setSelectionLoading(false);
    }, 600);

  }, [handleUserChange, user?.email]);

  const getScheduledata = useCallback(async () => {
    const candidate_Email = user?.email;
    if (!candidate_Email) return;

    try {
      const refreshedToken = user?.getIdToken ? await user.getIdToken() : null;
      if (!refreshedToken) return;
      const headers = { Authorization: `Bearer ${refreshedToken}` };

      const scheduledReq = axios.post(`${process.env.REACT_APP_CANDIDATE}/getCandidateDetailsRegistration`, { email: candidate_Email }, { headers }).catch(() => null);

      const previousTestsReq = axios.post(`${process.env.REACT_APP_LAMBDA_WEB}/get-previous-tests`, { email: candidate_Email }, { headers }).catch(() => null);

      const [scheduledRes, previousTestsRes] = await Promise.all([scheduledReq, previousTestsReq]);

      let combinedCandidates = [];

      if (scheduledRes?.data?.statusCode === 200) {
        combinedCandidates = (scheduledRes?.data?.candidate || []).map(cand => ({ ...cand, source: 'scheduled' }));
      }

      if (previousTestsRes?.data && Array.isArray(previousTestsRes.data)) {
        setIsInterviewCompleted(previousTestsRes.data.length > 0);
        const previousTests = previousTestsRes.data.map(cand => ({ ...cand, source: 'previous' }));
        combinedCandidates = [...combinedCandidates, ...previousTests];
      }

      // Filter to keep unique items by requisition_id
      const uniqueItems = new Map();
      combinedCandidates.forEach(cand => {
        if (cand.requisition_id && !uniqueItems.has(cand.requisition_id)) {
          uniqueItems.set(cand.requisition_id, cand);
        }
      });

      const uniqueCandidatesList = Array.from(uniqueItems.values());

      setCandidateList(uniqueCandidatesList);
      if (uniqueCandidatesList.length > 0) {
        // Smart Selection Logic: Prioritize the requisition the user just registered/landed for
        const currentReqId = userData?.getReqIdValue || profile?.userfetchedData?.requisition_id;

        let initialIndex = 0;
        if (currentReqId) {
          const matchingIndex = uniqueCandidatesList.findIndex(cand =>
            String(cand.requisition_id) === String(currentReqId) && cand.source === 'scheduled'
          );
          if (matchingIndex !== -1) {
            initialIndex = matchingIndex;
          } else {
            // Fallback: try to find any 'scheduled' candidate if specific match not found
            const anyScheduledIndex = uniqueCandidatesList.findIndex(cand => cand.source === 'scheduled');
            if (anyScheduledIndex !== -1) initialIndex = anyScheduledIndex;
          }
        }

        handleCandidateSelection(initialIndex, uniqueCandidatesList, true);

        // Show success toast if coming from registration
        if (location.state?.fromRegistration && !toastShown.current) {
          const firstCand = uniqueCandidatesList[0];
          const reqId = firstCand?.requisition_id;
          const intDate = firstCand?.interview_date ? moment(firstCand.interview_date).format('DD-MM-YYYY') : '';

          if (reqId && intDate) {
            toast.success(`You are now registered for the VEREQ ID ${reqId}, and your interview is scheduled for ${intDate}.`);
            toastShown.current = true;
            // Clear location state to prevent toast on refresh
            navigate(location.pathname, { replace: true, state: {} });
          }
        }
      }

    } catch (error) {
      logSentryException(error);
      console.error('Error fetching schedule data:', error);
    }
  }, [user, handleCandidateSelection]);

  const checkInterviewAndReschedule = useCallback(async (getUserdata, reqId, interviewType) => {
    try {
      const refreshedToken = await user?.getIdToken();
      const response = await axios.post(`${process.env.REACT_APP_CANDIDATE}/getCandidateDetailsRegistration`, {
        email: user?.email
      }, {
        headers: {
          Authorization: `Bearer ${refreshedToken}`
        }
      });
      if (response?.data?.statusCode === 200) {
        // const getRequisitionfor = response?.data?.candidate[0]?.requisition_for;
        // handleUserChange("requisition_for", getRequisitionfor);
        const getCandidateData = response?.data?.candidate[0]
        const { status } = isInterviewWithin48Hours(
          getCandidateData?.interview_date,
          getCandidateData?.interview_time
        )
        // Agar expired hai toh naya interview schedule karo
        if (status === 'expired') {
          await interviewSchedule(getUserdata, reqId, interviewType)
          // Schedule hone ke baad fresh data fetch karo
          await getScheduledata()
        }
      }
    } catch (error) {
      logSentryException(error);
      console.error('Error checking/rescheduling interview:', error);
    }
  }, [user, interviewSchedule, getScheduledata]);

  const fetchUserInfo = useCallback(async () => {
    try {
      const refreshedToken = user?.getIdToken ? await user.getIdToken() : null;
      if (!refreshedToken) return;
      const headers = {
        'Authorization': `Bearer ${refreshedToken}`,
        'Content-Type': 'application/json',
      };
      const response = await axios.post(`${process.env.REACT_APP_FRONTEND}/api/v4.0/candidate-info/fetchuser`, {
        operation: 'fetchuser',
        email: user?.email,
      }, { headers });

      const getuserData = response.data.data[0];
      // Strict checks: all three must be true for Step 1 to be considered complete
      const hasPhoto = !!(getuserData?.user_photo?.trim());
      const hasResume = !!(getuserData?.resume_url?.trim());
      const isNumberVerified = getuserData?.is_number_verified === 1;
      const verified = hasPhoto && hasResume && isNumberVerified;
      const empAdded = getuserData?.is_employement_added === 1;
      const eduAdded = getuserData?.is_edu_added === 1;
      const headlineAdded = getuserData?.resume_headline_added === 1;

      setProfile((prev) => ({
        ...prev,
        prefered_lang: getuserData?.prefered_lang,
        is_profile_verified: getuserData?.resume_headline_added,
        userfetchedData: getuserData
      }));

      setVerification(verified);
      setOtherVerified(empAdded);
      setEducationVerified(eduAdded);
      setHeadlineVerified(headlineAdded);
      setIsAppDownloaded(getuserData?.is_app_downloaded === 1);

      // ✅ Sync progress bar
      if (verified && empAdded && eduAdded && headlineAdded) {
        setIsCompleted(true);
        setCurrentStep(2);   // final step in 2-step flow
        // ✅ Revalidation check: if profile is fully complete & 3+ months passed
        if (isRevalidationRequired(getuserData?.last_data_updated_at || getuserData?.updated_at)) {
          setShowRevalidationModal(true);
        }
      } else {
        setIsCompleted(false);  // ✅ VERY IMPORTANT
        setCurrentStep(1);
      }

      if (empAdded && eduAdded && headlineAdded && !isRevalidationRequired(getuserData?.last_data_updated_at || getuserData?.updated_at)) {
        const reqId = userData?.getReqId;
        const interviewType = userData?.getInterviewtype;
        const getExistvalue = await checkAlreadyExist(reqId);
        if (getExistvalue === 0) {
          await interviewSchedule(getuserData, reqId, interviewType);
        } else {
          // Candidate exists — check if interview expired, if yes reschedule
          await checkInterviewAndReschedule(getuserData, reqId, interviewType);
        }
      }
    } catch (error) {
      logSentryException(error);
      console.error(error);
    }
  }, [user, setProfile, setIsCompleted, setCurrentStep, checkAlreadyExist, interviewSchedule, checkInterviewAndReschedule, userData?.getReqId, userData?.getInterviewtype]);

  const fetchtestResult = useCallback(async () => {
    try {
      const refreshedToken = user?.getIdToken ? await user.getIdToken() : null;
      if (!refreshedToken) return;
      const headers = {
        'Authorization': `Bearer ${refreshedToken}`,
        'Content-Type': 'application/json',
      };
      await axios.post(`${process.env.REACT_APP_FRONTEND}/api/v4.0/candidate-info/fetchuser`, {
        operation: 'fetchtest',
        email: user?.email,
      }, { headers });
    } catch (error) {
      logSentryException(error);
    }
  }, [user]);

  useEffect(() => {
    if (!user?.uid) return;
    if (hasInitialized.current && lastUid.current === user.uid) return;

    // Set synchronously to prevent race conditions
    hasInitialized.current = true;
    lastUid.current = user.uid;

    const init = async () => {
      setPageLoading(true);
      try {
        await Promise.all([fetchUserInfo(), fetchtestResult(), getScheduledata()]);
      } catch (error) {
        logSentryException(error);
        console.error("Initial load error:", error);
      } finally {
        setPageLoading(false);
      }
    };
    init();
  }, [user?.uid, fetchUserInfo, fetchtestResult, getScheduledata]);


  const [mediaStream] = useState(null);
  useEffect(() => {
    return () => {
      if (mediaStream) mediaStream.getTracks().forEach(track => track.stop());
    };
  }, [mediaStream]);


  // ✅ FIX: Navigate to correct page based on what's missing
  const ProfileVerifyPage = () => {
    logFieldToSentry('Continue registration', { isVerified, isOtherVerified, isEducationVerified, isHeadlineVerified });

    handleUserChange('currentStep', 1)

    if (isVerified) {
      handleUserChange('currentStep', 2)
    }

    if (!isVerified || !isOtherVerified || !isEducationVerified || !isHeadlineVerified)
      navigate('/profile-verify');
    else
      navigate('/');
  };

  // ✅ Edit profile handler — opens review flow without scheduling
  const handleEditProfile = (startStep) => {
    logFieldToSentry('Edit profile', { startStep });
    sessionStorage.setItem('editMode', 'true');
    sessionStorage.removeItem('revalidationMode');
    if (startStep === 1) {
      setCurrentStep(1);
      navigate('/profile-verify', { state: { editMode: true } });
    } else {
      setCurrentStep(startStep);
      navigate('/profile-verify', { state: { editMode: true, editStartStep: startStep } });
    }
  };


  const shortName = user?.displayName?.split(" ")?.map((n) => n[0]);

  // Reschedule date constraints
  const selectedCandidate = candidateList[selectedCandidateIndex];
  const candidateInterviewDate = selectedCandidate?.interview_date;

  // ✅ Sync Step 7 (Interview completion) data to StepContext whenever selectedCandidate changes
  useEffect(() => {
    if (selectedCandidate) {
      setInterviewData({
        is_technical: selectedCandidate.is_technical || 0,
        is_one2one: selectedCandidate.is_one2one || 0,
        is_tech_done: selectedCandidate.is_tech_done || 0,
        is_onetoone_done: selectedCandidate.is_onetoone_done || 0
      });
    }
  }, [selectedCandidate, setInterviewData]);

  const rescheduleMinDate = useMemo(() => {
    const today = moment().startOf('day');
    return today.format('YYYY-MM-DD');
  }, []);

  const rescheduleMaxDate = useMemo(() => {
    if (!candidateInterviewDate) return null;
    // maxDate = interview_date + 3 days
    const dateStr = String(candidateInterviewDate).split('T')[0];
    const interviewDatePlus3 = moment(dateStr, 'YYYY-MM-DD').add(3, 'days');
    const today = moment().startOf('day');
    // If interview_date + 3 days is before today, no dates available
    if (interviewDatePlus3.isBefore(today)) return null;
    return interviewDatePlus3.format('YYYY-MM-DD');
  }, [candidateInterviewDate]);

  const isRescheduleDisabled = useMemo(() => {
    if (!candidateInterviewDate) return true;
    // Disable rescheduling if interview_date + 3 days has passed
    const dateStr = String(candidateInterviewDate).split('T')[0];
    const interviewDatePlus3 = moment(dateStr, 'YYYY-MM-DD').add(3, 'days');
    const today = moment().startOf('day');
    return interviewDatePlus3.isBefore(today);
  }, [candidateInterviewDate]);

  const rescheduleWindowText = useMemo(() => {
    if (!rescheduleMaxDate) return null;
    const maxMoment = moment(rescheduleMaxDate);
    const today = moment().startOf('day');
    const days = maxMoment.diff(today, 'days');
    return { maxDateFormatted: maxMoment.format('DD MMM YYYY'), days };
  }, [rescheduleMaxDate]);

  const isSelectedDateTimePast = useMemo(() => {
    if (!rescheduleDate) return false;
    let hour = rescheduleHour;
    if (rescheduleAmPm === 'AM') {
      if (hour === 12) hour = 0;
    } else {
      if (hour !== 12) hour += 12;
    }
    const selected = moment(
      `${rescheduleDate} ${String(hour).padStart(2, '0')}:${String(rescheduleMinute).padStart(2, '0')}`,
      'YYYY-MM-DD HH:mm'
    );
    return selected.isBefore(moment());
  }, [rescheduleDate, rescheduleHour, rescheduleMinute, rescheduleAmPm]);

  // Derive interview scenario for Card 2 & Card 3 rendering
  // Priority: Case 4 → Case 7 → Case 6 → Case 5 → Case 3 → Case 2 → Case 1
  const interviewScenario = useMemo(() => {
    if (!interviewStatus || !selectedCandidate) return null;

    const hasOneToOne = selectedCandidate?.is_one2one === 1;
    const hasTechnical = selectedCandidate?.is_technical === 1;
    const oneToOneDone = selectedCandidate?.is_onetoone_done === 1;
    const techDone = selectedCandidate?.is_tech_done === 1;

    // Case 4: All applicable rounds completed → Interview Completed
    const allDone =
      (hasOneToOne && oneToOneDone && hasTechnical && techDone) ||
      (!hasOneToOne && hasTechnical && techDone) ||
      (hasOneToOne && oneToOneDone && !hasTechnical);
    if (allDone || interviewStatus === "completed") return "completed";

    // Case 7: interview_date + 3 days crossed → Hard Expired, contact support
    const rawIntDate = selectedCandidate?.interview_date;
    if (rawIntDate) {
      const dateStr = String(rawIntDate).split('T')[0];
      const interviewDatePlus3 = moment(dateStr, 'YYYY-MM-DD').add(3, 'days');
      const today = moment().startOf('day');
      if (interviewDatePlus3.isBefore(today)) {
        return "beyond_3day_expired";
      }
    }

    // Case 6: 48hr expired + is_rescheduled === 1 → Expired, contact support
    if (interviewStatus === "expired" && selectedCandidate?.is_rescheduled === 1) return "expired_no_reschedule";

    // Case 5: 48hr expired + is_rescheduled === 0 + within 3-day window → Expired with Reschedule
    if (interviewStatus === "expired") return "expired_reschedule";

    // Case 3: Within 48hr + partial round completion
    if (
      hasOneToOne &&
      hasTechnical &&
      ((oneToOneDone && !techDone) || (!oneToOneDone && techDone))
    ) {
      return "partial";
    }

    // Case 2: Within 48hr + is_rescheduled === 1 → Active, no reschedule
    if (interviewStatus === "active" && selectedCandidate?.is_rescheduled === 1) return "active_no_reschedule";

    // Case 1: Within 48hr + is_rescheduled === 0 → Active with reschedule
    if (interviewStatus === "active") return "active_reschedule";

    return null;
  }, [interviewStatus, selectedCandidate]);

  const handleOpenReschedule = () => {
    logFieldToSentry('Open reschedule modal', true);
    setRescheduleDate('');
    setRescheduleAcknowledged(false);
    // Pre-fill time from candidate's existing interview_time
    const rawTime = selectedCandidate?.interview_time || '00:00:00';
    const timeParts = rawTime.split('.');
    const cleanTime = timeParts[0];
    const [hh, mm] = cleanTime.split(':').map(Number);
    const h12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
    setRescheduleHour(h12);
    setRescheduleMinute(mm || 0);
    setRescheduleAmPm(hh >= 12 ? 'PM' : 'AM');
    setShowRescheduleModal(true);
  };

  const handleConfirmReschedule = async () => {
    if (isSelectedDateTimePast) {
      toast.error('Selected date and time cannot be in the past.');
      return;
    }
    try {
      const refreshedToken = user?.getIdToken ? await user.getIdToken() : null;
      if (!refreshedToken) return;
      const headers = {
        'Authorization': `Bearer ${refreshedToken}`,
        'Content-Type': 'application/json',
      };
      const formattedTime = `${String(rescheduleHour).padStart(2, '0')}:${String(rescheduleMinute).padStart(2, '0')} ${rescheduleAmPm}`;
      const payload = {
        email: user?.email,
        newDate: rescheduleDate,
        newTime: formattedTime,
        requisitionId: selectedCandidate?.requisition_id,
      };
      const response = await axios.post(
        `${process.env.REACT_APP_CANDIDATE}/rescheduleInterview`,
        payload,
        { headers }
      );
      if (response.data?.statusCode === 200) {
        toast.success('Interview rescheduled successfully');
        setShowRescheduleModal(false);
        await getScheduledata();
      } else {
        toast.error(response.data?.errorMessage || 'Failed to reschedule interview');
      }
    } catch (error) {
      logSentryException(error, { payload: { rescheduleDate, rescheduleHour, rescheduleMinute, rescheduleAmPm } });
      console.error('Error rescheduling interview:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  // Steps data JSON by VA
  const stepsData = useMemo(() => [
    {
      id: 1,
      title: "Verify Your Profile",
      description: "Upload your photo and CV, then verify your phone number with a quick OTP. Takes about 3 minutes.",
      lockedMsg: "Locked",
      checkverify: checkProfile
    },
    {
      id: 2,
      title: "Complete Your Details",
      description: "Add employment, education and headline information so recruiters can evaluate your profile.",
      lockedMsg: "Unlocks after Step 1",
      checkverify: (!isOtherVerified || !isEducationVerified || !isHeadlineVerified) ? 0 : 1
    },
    {
      id: 3,
      title: "Download Interview App",
      description: "Download our lightweight windows desktop app to take your AI-powered interview.",
      lockedMsg: "Unlocks after Step 2",
      checkverify: isAppDownloaded ? 1 : 0
    },
    {
      id: 4,
      title: "Take Your Interview",
      description: "Launch the app within scheduled time, no waiting. Complete at your own pace from home.",
      lockedMsg: "Unlocks after downloads",
      checkverify: isInterviewCompleted ? 1 : 0
    },
  ], [checkProfile, isOtherVerified, isEducationVerified, isHeadlineVerified, isAppDownloaded, isInterviewCompleted])

  // Custom Countdown Timer By VA
  const CountdownTimer = ({ totalSeconds, onTimerEnd, compact = false }) => {
    const [secondsLeft, setSecondsLeft] = useState(totalSeconds);

    // totalSeconds update hone pe reset karo
    useEffect(() => {
      setSecondsLeft(totalSeconds);
    }, [totalSeconds]);

    useEffect(() => {
      if (secondsLeft <= 0) {
        if (onTimerEnd) onTimerEnd();
        return;
      }

      const interval = setInterval(() => {
        setSecondsLeft((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(interval);
    }, [secondsLeft, onTimerEnd]);

    const formatTime = (secs) => {
      const hrs = Math.floor(secs / 3600);
      const mins = Math.floor((secs % 3600) / 60);
      const sec = secs % 60;
      return {
        hrs: String(hrs).padStart(2, "0"),
        mins: String(mins).padStart(2, "0"),
        sec: String(sec).padStart(2, "0")
      };
    };

    const time = formatTime(secondsLeft);

    if (compact) {
      return (
        <span className={`fw-bold ${secondsLeft === 0 ? "text-deep-purple" : ""}`}>
          {time.hrs}:{time.mins}:{time.sec}
        </span>
      );
    }

    return (
      <div className={`d-flex align-items-start justify-content-center gap-2 mt-2 ${secondsLeft === 0 ? "text-success" : "text-warning"}`}>
        <div className="d-flex flex-column align-items-center">
          <span className="fs-3 fw-bold lh-1" style={{ letterSpacing: '2px' }}>{time.hrs}</span>
          <span className="fs-12 text-white text-opacity-75">hrs</span>
        </div>
        <span className="fs-3 fw-bold lh-sm" style={{ marginTop: '-4px' }}>:</span>
        <div className="d-flex flex-column align-items-center">
          <span className="fs-3 fw-bold lh-1" style={{ letterSpacing: '2px' }}>{time.mins}</span>
          <span className="fs-12 text-white text-opacity-75">min</span>
        </div>
        <span className="fs-3 fw-bold lh-sm" style={{ marginTop: '-4px' }}>:</span>
        <div className="d-flex flex-column align-items-center">
          <span className="fs-3 fw-bold lh-1" style={{ letterSpacing: '2px' }}>{time.sec}</span>
          <span className="fs-12 text-white text-opacity-75">sec</span>
        </div>
      </div>
    );
  };


  const isInterviewWithin48Hours = (interview_date, interview_time) => {
    if (!interview_date || !interview_time) return { status: 'expired', remainingSeconds: 0, interviewDateTimeIST: null };

    const dateStr = interview_date.split('T')[0];
    const timeStr = interview_time.split('.')[0];

    // Parse the date strictly using moment
    const interviewDateTimeIST = moment(`${dateStr} ${timeStr}`, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"]);

    // Window end = interview time + exactly 48 hours
    const interviewEndTime = interviewDateTimeIST.clone().add(48, 'hours');

    const now = moment();
    const remainingSeconds = interviewEndTime.diff(now, 'seconds');

    // Case 1: Interview abhi aaya nahi — date dikhao (e.g. 1-03-2026 ko hai toh date show karo)
    if (now.isBefore(interviewDateTimeIST)) {
      return { status: 'active', remainingSeconds: remainingSeconds, interviewDateTimeIST: interviewDateTimeIST.toDate() }
    }
    // Case 2: Interview time aa gaya aur 48 hour window active hai — timer dikhao
    else if (now.isBetween(interviewDateTimeIST, interviewEndTime) && remainingSeconds > 0) {
      return { status: 'active', remainingSeconds: remainingSeconds > 0 ? remainingSeconds : 0, interviewDateTimeIST: interviewDateTimeIST.toDate() }
    }
    // Case 3: 48 hour window khatam ho gaya — HR se reschedule karwao
    else {
      return { status: 'expired', remainingSeconds: 0, interviewDateTimeIST: interviewDateTimeIST.toDate() }
    }
  }

  const newwisInterviewWithin48Hours = (interview_date, interview_time) => {
    if (!interview_date) return { status: 'expired', remainingSeconds: 0, interviewDateTimeIST: null };

    // ✅ interview_date already full UTC ISO hai — directly parse karo
    // "2026-03-23T13:43:00.000Z" → moment directly handle karega
    const interviewDateTimeIST = moment(interview_date);

    const interviewEndTime = interviewDateTimeIST.clone().add(48, 'hours');
    const now = moment();
    const remainingSeconds = interviewEndTime.diff(now, 'seconds');

    if (now.isBefore(interviewDateTimeIST)) {
      return { status: 'active', remainingSeconds: remainingSeconds, interviewDateTimeIST: interviewDateTimeIST.toDate() };
    } else if (remainingSeconds > 0) {
      return { status: 'active', remainingSeconds: remainingSeconds, interviewDateTimeIST: interviewDateTimeIST.toDate() };
    } else {
      return { status: 'expired', remainingSeconds: 0, interviewDateTimeIST: interviewDateTimeIST.toDate() };
    }
  };

  // Removed separate effect as it's now in main init

  // Auto-redirect to complete-profile after Step 1 is done
  useEffect(() => {
    if (checkProfile === 1 && (!isOtherVerified || !isEducationVerified || !isHeadlineVerified)) {
      const timer = setTimeout(() => {
        navigate('/complete-profile');
      }, 3000); // 3 seconds delay
      return () => clearTimeout(timer);
    }
  }, [checkProfile, isOtherVerified, isEducationVerified, isHeadlineVerified, navigate])


  return (
    <div className="profile-page py-3">
      <div className="container">
        {pageLoading ? (
          <div className="info-wrapper">
            {/* Loader banner */}
            <div className="alert alert-success d-flex align-items-center gap-2 rounded-3 mb-3 py-2" role="alert">
              <div className="spinner-border spinner-border-sm text-success" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <span>
                  Preparing, please wait...
                </span>
              </div>
            </div>
            {/* Steps cards */}
            <div className="row g-3 mb-4">
              {stepsData.map((step) => (
                <div className="col-md-6" key={step.id}>
                  <div
                    className={`info-card bg-white h-100 rounded-4 border border-2 p-3 p-xxl-4 ${step.id < completedStep
                      ? "border-theme6"
                      : step.id === completedStep
                        ? "border-primary"
                        : "border-white opacity-75 text-muted"
                      }`}>
                    <div
                      className={`icon bg-opacity-25 d-flex align-items-center justify-content-center fw-semibold rounded-3 ${step.id < completedStep
                        ? "bg-theme6 text-theme6"
                        : step.id === completedStep
                          ? "bg-primary text-primary"
                          : "bg-secondary text-secondary"
                        }`}
                      style={{ width: "30px", height: "30px", marginBottom: "10px" }}>
                      {step.id < completedStep ? "✓" : step.id}
                    </div>
                    <div className="info-box mb-2 pb-1">
                      <div className="fs-16 fw-semibold text-dark mb-1">
                        {step.title}
                      </div>
                      <div className="fs-14 text-muted">{step.description}</div>
                    </div>
                    {step.id < completedStep && (
                      <span className="badge bg-theme6 bg-opacity-10 border border-theme6 rounded-pill text-theme6 py-1">
                        <i className="bi bi-check-circle-fill"></i> Completed
                      </span>
                    )}
                    {step.id === completedStep && (
                      <span className="badge bg-primary bg-opacity-10 border border-primary rounded-pill text-primary py-1">
                        <i className="bi bi-circle-fill"></i> Your Next Step
                      </span>
                    )}
                    {step.id > completedStep && (
                      <span className="badge bg-secondary bg-opacity-10 border rounded-pill text-secondary py-1">
                        🔒 {step.lockedMsg}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="info-wrapper">
            {/* Header: User Greeting */}
            <div className="account-header d-flex align-items-center gap-2 mb-3 mb-xxl-4">
              <div className="user-info d-flex align-items-center gap-2">
                <div
                  className="chip-img bg-primary bg-opacity-25 border border-2 border-primary d-flex align-items-center justify-content-center rounded-circle overflow-hidden"
                  style={{ height: "40px", width: "40px" }}>
                  <div className="fs-6 fw-bold user-shortname text-primary">
                    {shortName}
                  </div>
                </div>
                <div className="wrapper text-black text-start ms-1">
                  <div className="small text-muted">Welcome,</div>
                  <div className="username main-heading text-dark lh-sm">
                    {user?.displayName}
                  </div>
                </div>
              </div>
            </div>

            {/* Three Info Cards Row */}
            {isProfileComplete && (
              <div className="row g-3 mb-3 mb-xxl-4">
                {/* Card 1: Applied Requisition */}
                <div className="col-lg-4">
                  <div className="info-card bg-white h-100 rounded-4 border p-3 p-xxl-4">
                    {/* Card Label */}
                    <div className="small text-muted text-uppercase fw-medium mb-3" style={{ letterSpacing: '0.5px' }}>
                      Applied Requisition
                    </div>

                    {candidateList.length > 0 && selectedCandidate ? (
                      <>
                        {/* Requisition Selector Box */}
                        <div className="position-relative mb-3">
                          <select
                            className="form-select rounded-3 border-0 pe-5 py-3"
                            value={selectedCandidateIndex}
                            onChange={(e) => handleCandidateSelection(Number(e.target.value), candidateList)}
                            style={{
                              cursor: "pointer",
                              boxShadow: "none",
                              backgroundColor: "#f3f0ff",
                              appearance: "none",
                              WebkitAppearance: "none",
                              MozAppearance: "none",
                            }}>
                            {candidateList.map((cand, idx) => (
                              <option key={cand.id || idx} value={idx} className="text-dark bg-white">
                                VEREQ{cand.requisition_id} — {cand.requisitionDetails?.jobTitle || cand.user_name}
                              </option>
                            ))}
                          </select>
                          {/* Custom display overlay */}
                          <div
                            className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center rounded-3 px-3"
                            style={{ backgroundColor: "#f3f0ff", pointerEvents: "none" }}>
                            <div className="fw-bold text-primary">
                              VEREQ{selectedCandidate?.requisition_id}
                            </div>
                            <div className="text-dark small">
                              {selectedCandidate?.requisitionDetails?.jobTitle || '—'}
                            </div>
                          </div>
                          {/* Chevron Icon */}
                          <i
                            className="bi bi-chevron-down position-absolute text-secondary"
                            style={{ right: "12px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
                          ></i>
                        </div>

                        {/* Round Tags */}
                        <div className="d-flex flex-wrap gap-2">
                          {selectedCandidate?.is_one2one === 1 && (
                            <span className="badge rounded-pill py-2 px-3" style={{ backgroundColor: "#ede9fe", color: "#7c3aed" }}>
                              Sheela AI
                            </span>
                          )}
                          {selectedCandidate?.is_technical === 1 && (
                            <span className="badge rounded-pill py-2 px-3" style={{ backgroundColor: "#ede9fe", color: "#7c3aed" }}>
                              Technical Round
                            </span>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="text-center text-muted py-4">
                        <i className="bi bi-briefcase fs-3 d-block mb-2"></i>
                        <div className="small">No requisition applied yet</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card 2: Interview Status */}
                <div className="col-lg-4">
                  {candidateList.length > 0 && selectedCandidate ? (
                    selectionLoading ? (
                      <div className="info-card h-100 rounded-4 p-3 p-xxl-4" style={{ backgroundColor: '#f8f7fc', border: '1px solid #e9e6f2' }}>
                        <div className="d-flex align-items-center justify-content-center py-5">
                          <div className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Scenario 1: Completed */}
                        {interviewScenario === "completed" && (
                          <div className="info-card h-100 rounded-4 p-3 p-xxl-4" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                            <div className="small text-uppercase fw-semibold mb-3" style={{ letterSpacing: '0.5px', color: '#10b981' }}>
                              Interview Status
                            </div>
                            <div className="d-flex align-items-start gap-3">
                              <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0" style={{ width: '40px', height: '40px', backgroundColor: '#d1fae5' }}>
                                <i className="bi bi-check-circle-fill" style={{ color: '#10b981', fontSize: '20px' }}></i>
                              </div>
                              <div>
                                <div className="fw-bold text-dark" style={{ fontSize: '18px' }}>Interview completed</div>
                                <div className="small text-muted mt-1">
                                  Your interview on {interviewDateTime && moment(interviewDateTime).format("DD MMM YYYY")} is complete.
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Scenario 2: Partial Interview Completed */}
                        {interviewScenario === "partial" && (
                          <div className="info-card h-100 rounded-4 p-3 p-xxl-4" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
                            <div className="small text-uppercase fw-semibold mb-3" style={{ letterSpacing: '0.5px', color: '#f59e0b' }}>
                              Interview Status
                            </div>
                            <div className="d-flex align-items-start gap-3">
                              <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0" style={{ width: '40px', height: '40px', backgroundColor: '#fef3c7' }}>
                                <i className="bi bi-hourglass-split" style={{ color: '#f59e0b', fontSize: '20px' }}></i>
                              </div>
                              <div>
                                <div className="fw-bold text-dark" style={{ fontSize: '18px' }}>Partial Interview completed</div>
                                <div className="small text-muted mt-1">
                                  You have completed one round, and one round is pending.
                                </div>
                                <div className="small text-muted mt-2">
                                  Complete your second round interview before <span className="fw-semibold text-dark">{interviewDateTime && moment(interviewDateTime).add(48, 'hours').format("DD MMM YYYY, hh:mm A")}</span>.
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Scenario: Beyond 3-Day Reschedule Window — No Reschedule */}
                        {interviewScenario === "beyond_3day_expired" && (
                          <div className="info-card h-100 rounded-4 p-3 p-xxl-4" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
                            <div className="small text-uppercase text-danger fw-semibold mb-3" style={{ letterSpacing: '0.5px' }}>
                              Interview Window Expired
                            </div>
                            <div className="fs-18 fw-bold text-dark mb-1">
                              Expired on {interviewDateTime && moment(interviewDateTime).add(48, 'hours').format("DD MMM YYYY, hh:mm A")}
                            </div>
                            <div className="small text-muted fw-bold mb-3">
                              Scheduled on {interviewDateTime && moment(interviewDateTime).format("DD MMM YYYY")} • {interviewDateTime && moment(interviewDateTime).format("hh:mm A")}
                            </div>
                            <div className="small text-danger mb-2">
                              You cannot reschedule the interview, please contact support.
                            </div>
                          </div>
                        )}

                        {/* Scenario 3: Expired, No Reschedule Available */}
                        {interviewScenario === "expired_no_reschedule" && (
                          <div className="info-card h-100 rounded-4 p-3 p-xxl-4" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
                            <div className="small text-uppercase text-danger fw-semibold mb-3" style={{ letterSpacing: '0.5px' }}>
                              Interview Window Expired
                            </div>
                            <div className="fs-18 fw-bold text-dark mb-1">
                              Expired on {interviewDateTime && moment(interviewDateTime).add(48, 'hours').format("DD MMM YYYY, hh:mm A")}
                            </div>
                            <div className="small text-muted fw-bold mb-3">
                              Scheduled on {interviewDateTime && moment(interviewDateTime).format("DD MMM YYYY")} • {interviewDateTime && moment(interviewDateTime).format("hh:mm A")}
                            </div>
                            <div className="small text-danger mb-2">
                              You have already rescheduled this interview once. For further rescheduling, please contact support.
                            </div>
                          </div>
                        )}

                        {/* Scenario 4: Expired, Reschedule Available */}
                        {interviewScenario === "expired_reschedule" && (
                          <div className="info-card h-100 rounded-4 p-3 p-xxl-4" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
                            <div className="small text-uppercase text-danger fw-semibold mb-3" style={{ letterSpacing: '0.5px' }}>
                              Interview Window Expired
                            </div>
                            <div className="fs-18 fw-bold text-dark mb-1">
                              Expired on {interviewDateTime && moment(interviewDateTime).add(48, 'hours').format("DD MMM YYYY, hh:mm A")}
                            </div>
                            <div className="small fw-bold text-muted mb-3">
                              Scheduled on {interviewDateTime && moment(interviewDateTime).format("DD MMM YYYY")} • {interviewDateTime && moment(interviewDateTime).format("hh:mm A")}
                            </div>
                            <br />
                            {selectedCandidate?.isSheelaCandiate === true && (
                              <>
                                {!isRescheduleDisabled ? (
                                  <>
                                    <button
                                      className="btn w-100 rounded-3 py-2 mb-2"
                                      style={{ backgroundColor: 'transparent', border: '2px solid #374151', color: '#374151', fontWeight: '600' }}
                                      onClick={handleOpenReschedule}
                                    >
                                      Reschedule
                                    </button>
                                    {rescheduleWindowText?.days === 0 ? (
                                      <div className="small text-center" style={{ color: '#3b82f6' }}>
                                        Can reschedule for today.
                                      </div>
                                    ) : (
                                      <div className="small text-center" style={{ color: '#3b82f6' }}>
                                        Can reschedule till {rescheduleWindowText?.maxDateFormatted} ({rescheduleWindowText?.days}-day window)
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="small text-muted">
                                    Please contact <a style={{ color: '#7c3aed', textDecoration: 'underline' }}>Support</a> to request a new interview window.
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}

                        {/* Scenario 5: Active, Not Yet Rescheduled */}
                        {interviewScenario === "active_reschedule" && (
                          <div className="info-card h-100 rounded-4 p-3 p-xxl-4" style={{ backgroundColor: '#f7fbff', border: '1px solid #e2e8f0' }}>
                            <div className="small text-uppercase fw-semibold mb-3" style={{ letterSpacing: '0.5px', color: '#275bda' }}>
                              Interview Status
                            </div>
                            <div className="fs-18 fw-bold text-dark mb-1">
                              Interview scheduled
                            </div>
                            <div className="small text-muted fw-bold mb-3">
                              {interviewDateTime && moment(interviewDateTime).format("DD MMM YYYY")} • {interviewDateTime && moment(interviewDateTime).format("hh:mm A")}
                            </div>
                            <div className="small text-muted mb-3">
                              Please complete your interview within the 48-hour window. Your deadline is <span className="fw-semibold text-dark">{interviewDateTime && moment(interviewDateTime).add(48, 'hours').format("DD MMM YYYY, hh:mm A")}</span>.
                            </div>
                            {selectedCandidate?.isSheelaCandiate === true && (
                              <>
                                {!isRescheduleDisabled ? (
                                  <>
                                    <button
                                      className="btn w-100 rounded-3 py-2 mb-2"
                                      style={{ backgroundColor: 'transparent', border: '2px solid #374151', color: '#374151', fontWeight: '600' }}
                                      onClick={handleOpenReschedule}
                                    >
                                      Reschedule
                                    </button>
                                    {rescheduleWindowText?.days === 0 ? (
                                      <div className="small text-center" style={{ color: '#3b82f6' }}>
                                        Can reschedule for today.
                                      </div>
                                    ) : (
                                      <div className="small text-center" style={{ color: '#3b82f6' }}>
                                        Can reschedule till {rescheduleWindowText?.maxDateFormatted} ({rescheduleWindowText?.days}-day window)
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="small text-muted">
                                    Please contact <a style={{ color: '#7c3aed', textDecoration: 'underline' }}>Support</a> to request a new interview window.
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}

                        {/* Scenario 6: Active, Already Rescheduled Once */}
                        {interviewScenario === "active_no_reschedule" && (
                          <div className="info-card h-100 rounded-4 p-3 p-xxl-4" style={{ backgroundColor: '#f7fbff', border: '1px solid #e2e8f0' }}>
                            <div className="small text-uppercase fw-semibold mb-3" style={{ letterSpacing: '0.5px', color: '#275bda' }}>
                              Interview Status
                            </div>
                            <div className="fs-18 fw-bold text-dark mb-1">
                              Interview scheduled
                            </div>
                            <div className="small fw-bold text-muted mb-3">
                              {interviewDateTime && moment(interviewDateTime).format("DD MMM YYYY")} • {interviewDateTime && moment(interviewDateTime).format("hh:mm A")}
                            </div>
                            <div className="small text-muted mb-3">
                              Please complete your interview within the 48-hour window. Your deadline is <span className="fw-semibold text-dark">{interviewDateTime && moment(interviewDateTime).add(48, 'hours').format("DD MMM YYYY, hh:mm A")}</span>.
                            </div>

                            <hr className="my-0" style={{ borderColor: '#e2e8f0' }} />

                            <div className="small text-danger mb-2">
                              You have already rescheduled this interview once. For further rescheduling, please contact support.
                            </div>
                          </div>
                        )}

                        {/* No scenario matched */}
                        {!interviewScenario && (
                          <div className="info-card h-100 rounded-4 p-3 p-xxl-4" style={{ backgroundColor: '#f8f7fc', border: '1px solid #e9e6f2' }}>
                            <div className="text-center text-muted py-4">
                              <i className="bi bi-info-circle me-1"></i>No interview scheduled yet
                            </div>
                          </div>
                        )}
                      </>
                    )
                  ) : (
                    <div className="info-card h-100 rounded-4 p-3 p-xxl-4" style={{ backgroundColor: '#f8f7fc', border: '1px solid #e9e6f2' }}>
                      <div className="text-center text-muted py-4">
                        <i className="bi bi-calendar-check fs-3 d-block mb-2"></i>
                        <div className="small">No interview scheduled</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Card 3: Round Details */}
                <div className="col-lg-4">
                  <div className="info-card bg-white h-100 rounded-4 border p-3 p-xxl-4">
                    <div className="small text-uppercase  text-muted fw-semibold mb-3" style={{ letterSpacing: '0.5px' }}>
                      Round Details
                    </div>

                    {candidateList.length > 0 && selectedCandidate ? (
                      <>
                        {/* Scenario 1: Completed */}
                        {interviewScenario === "completed" && (
                          <div className="d-flex flex-column gap-3">
                            {selectedCandidate?.is_one2one === 1 && (
                              <div className="d-grid align-items-center gap-2" style={{ gridTemplateColumns: '32px 1fr 100px' }}>
                                <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0" style={{ width: '32px', height: '32px', backgroundColor: '#d1fae5' }}>
                                  <i className="bi bi-check-lg" style={{ color: '#10b981', fontSize: '16px' }}></i>
                                </div>
                                <div>
                                  <div className="fw-semibold text-dark">Interview Round 1:</div>
                                  <div className="small text-muted">Sheela AI Interview</div>
                                </div>
                                <span className="fs-12 fw-semibold text-end" style={{ color: '#10b981' }}>COMPLETED</span>
                              </div>
                            )}
                            {selectedCandidate?.is_technical === 1 && (
                              <div className="d-grid align-items-center gap-2" style={{ gridTemplateColumns: '32px 1fr 100px' }}>
                                <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0" style={{ width: '32px', height: '32px', backgroundColor: '#d1fae5' }}>
                                  <i className="bi bi-check-lg" style={{ color: '#10b981', fontSize: '16px' }}></i>
                                </div>
                                <div>
                                  <div className="fw-semibold text-dark">Interview Round 2:</div>
                                  <div className="small text-muted">Live Technical Assessment</div>
                                </div>
                                <span className="fs-12 fw-semibold text-end" style={{ color: '#10b981' }}>COMPLETED</span>
                              </div>
                            )}
                            {(selectedCandidate?.is_one2one === 1 && selectedCandidate?.is_technical === 1) && (
                              <div className="small mt-2 pt-2 border-top text-muted">
                                <i className="bi bi-info-circle me-1"></i>
                                There is no dependency between rounds; you can take them in any order. However, once you complete one round, you must complete the other within 48 hours.
                              </div>
                            )}
                          </div>
                        )}

                        {/* Scenario 2: Partial — one completed, one expiring with countdown */}
                        {interviewScenario === "partial" && (
                          <div className="d-flex flex-column gap-3">
                            {selectedCandidate?.is_one2one === 1 && (
                              <div className="d-grid align-items-center gap-2" style={{ gridTemplateColumns: '32px 1fr auto' }}>
                                <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                  style={{ width: '32px', height: '32px', backgroundColor: selectedCandidate?.is_onetoone_done === 1 ? '#d1fae5' : '#fef3c7' }}>
                                  {selectedCandidate?.is_onetoone_done === 1 ? (
                                    <i className="bi bi-check-lg" style={{ color: '#10b981', fontSize: '16px' }}></i>
                                  ) : (
                                    <span className="fw-bold" style={{ color: '#f59e0b', fontSize: '13px' }}>1</span>
                                  )}
                                </div>
                                <div>
                                  <div className="fw-semibold text-dark">Interview Round 1:</div>
                                  <div className="small text-muted">Sheela AI Interview</div>
                                </div>
                                {selectedCandidate?.is_onetoone_done === 1 ? (
                                  <span className="fs-12 fw-semibold text-end" style={{ color: '#10b981' }}>
                                    <i className="bi bi-check-circle-fill me-1"></i>COMPLETED
                                  </span>
                                ) : (
                                  <div className="d-flex flex-column align-items-end" style={{ color: '#f97316', fontVariantNumeric: 'tabular-nums' }}>
                                    <span className="fs-12 fw-semibold text-nowrap"><i className="bi bi-clock-fill me-1"></i>EXPIRES IN</span>
                                    <span className="fs-12 fw-bold"><CountdownTimer totalSeconds={timerSeconds} onTimerEnd={() => setInterviewStatus("expired")} compact={true} /></span>
                                  </div>
                                )}
                              </div>
                            )}
                            {selectedCandidate?.is_technical === 1 && (
                              <div className="d-grid align-items-center gap-2" style={{ gridTemplateColumns: '32px 1fr auto' }}>
                                <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                  style={{ width: '32px', height: '32px', backgroundColor: selectedCandidate?.is_tech_done === 1 ? '#d1fae5' : '#fef3c7' }}>
                                  {selectedCandidate?.is_tech_done === 1 ? (
                                    <i className="bi bi-check-lg" style={{ color: '#10b981', fontSize: '16px' }}></i>
                                  ) : (
                                    <span className="fw-bold" style={{ color: '#f59e0b', fontSize: '13px' }}>2</span>
                                  )}
                                </div>
                                <div>
                                  <div className="fw-semibold text-dark">Interview Round 2:</div>
                                  <div className="small text-muted">Live Technical Assessment</div>
                                </div>
                                {selectedCandidate?.is_tech_done === 1 ? (
                                  <span className="fs-12 fw-semibold text-end" style={{ color: '#10b981' }}>
                                    <i className="bi bi-check-circle-fill me-1"></i>COMPLETED
                                  </span>
                                ) : (
                                  <div className="d-flex flex-column align-items-end" style={{ color: '#f97316', fontVariantNumeric: 'tabular-nums' }}>
                                    <span className="fs-12 fw-semibold text-nowrap"><i className="bi bi-clock-fill me-1"></i>EXPIRES IN</span>
                                    <span className="fs-12 fw-bold"><CountdownTimer totalSeconds={timerSeconds} onTimerEnd={() => setInterviewStatus("expired")} compact={true} /></span>
                                  </div>
                                )}
                              </div>
                            )}
                            {(selectedCandidate?.is_one2one === 1 && selectedCandidate?.is_technical === 1) && (
                              <div className="small mt-2 pt-2 border-top text-muted">
                                <i className="bi bi-info-circle me-1"></i>
                                There is no dependency between rounds; you can take them in any order. However, once you complete one round, you must complete the other within 48 hours.
                              </div>
                            )}
                          </div>
                        )}

                        {/* Scenario 3 & 4 & beyond_3day_expired: Expired — all rounds show EXPIRED */}
                        {(interviewScenario === "expired_no_reschedule" || interviewScenario === "expired_reschedule" || interviewScenario === "beyond_3day_expired") && (
                          <div className="d-flex flex-column gap-3">
                            {selectedCandidate?.is_one2one === 1 && (
                              <div className="d-grid align-items-center gap-2" style={{ gridTemplateColumns: '32px 1fr 100px' }}>
                                <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0" style={{ width: '32px', height: '32px', backgroundColor: '#dbeafe' }}>
                                  <span className="fw-bold" style={{ color: '#3b82f6', fontSize: '13px' }}>1</span>
                                </div>
                                <div>
                                  <div className="fw-semibold text-dark">Interview Round 1:</div>
                                  <div className="small text-muted">Sheela AI Interview</div>
                                </div>
                                <span className="fs-12 fw-semibold text-danger text-end">EXPIRED</span>
                              </div>
                            )}
                            {selectedCandidate?.is_technical === 1 && (
                              <div className="d-grid align-items-center gap-2" style={{ gridTemplateColumns: '32px 1fr 100px' }}>
                                <div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0" style={{ width: '32px', height: '32px', backgroundColor: '#dbeafe' }}>
                                  <span className="fw-bold" style={{ color: '#3b82f6', fontSize: '13px' }}>2</span>
                                </div>
                                <div>
                                  <div className="fw-semibold text-dark">Interview Round 2:</div>
                                  <div className="small text-muted">Live Technical Assessment</div>
                                </div>
                                <span className="fs-12 fw-semibold text-danger text-end">EXPIRED</span>
                              </div>
                            )}
                            {(selectedCandidate?.is_one2one === 1 && selectedCandidate?.is_technical === 1) && (
                              <div className="small mt-2 pt-2 border-top text-muted">
                                <i className="bi bi-info-circle me-1"></i>
                                There is no dependency between rounds; you can take them in any order. However, once you complete one round, you must complete the other within 48 hours.
                              </div>
                            )}
                          </div>
                        )}

                        {/* Scenario 5 & 6: Active — all rounds show AVAILABLE */}
                        {(interviewScenario === "active_reschedule" || interviewScenario === "active_no_reschedule") && (
                          <div className="d-flex flex-column gap-3">
                            {selectedCandidate?.is_one2one === 1 && (
                              <div className="d-grid align-items-center gap-2" style={{ gridTemplateColumns: '32px 1fr 100px' }}>
                                <div className="bg-primary bg-opacity-10 d-flex align-items-center justify-content-center rounded-circle flex-shrink-0" style={{ width: '32px', height: '32px' }}>
                                  <span className="fw-bold text-primary" style={{ fontSize: '13px' }}>1</span>
                                </div>
                                <div>
                                  <div className="fw-semibold text-dark">Interview Round 1:</div>
                                  <div className="small text-muted">Sheela AI Interview</div>
                                </div>
                                <span className="fs-12 fw-semibold text-primary text-end">AVAILABLE</span>
                              </div>
                            )}
                            {selectedCandidate?.is_technical === 1 && (
                              <div className="d-grid align-items-center gap-2" style={{ gridTemplateColumns: '32px 1fr 100px' }}>
                                <div className="bg-primary bg-opacity-10 d-flex align-items-center justify-content-center rounded-circle flex-shrink-0" style={{ width: '32px', height: '32px' }}>
                                  <span className="fw-bold text-primary" style={{ fontSize: '13px' }}>2</span>
                                </div>
                                <div>
                                  <div className="fw-semibold text-dark">Interview Round 2:</div>
                                  <div className="small text-muted">Live Technical Assessment</div>
                                </div>
                                <span className="fs-12 fw-semibold text-primary text-end">AVAILABLE</span>
                              </div>
                            )}
                            {(selectedCandidate?.is_one2one === 1 && selectedCandidate?.is_technical === 1) && (
                              <div className="border-top small pt-3 text-muted">
                                <i className="bi bi-info-circle me-1"></i>
                                There is no dependency between rounds; you can take them in any order. However, once you complete one round, you must complete the other within 48 hours.
                              </div>
                            )}
                          </div>
                        )}

                        {/* No rounds assigned */}
                        {selectedCandidate?.is_one2one !== 1 && selectedCandidate?.is_technical !== 1 && (
                          <div className="text-center text-muted py-2">
                            <i className="bi bi-info-circle me-1"></i>No rounds assigned yet
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center text-muted py-4">
                        <i className="bi bi-list-check fs-3 d-block mb-2"></i>
                        <div className="small">No rounds assigned</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <section className="progress-section">
              <div className="fs-18 fw-bold text-dark mb-2">Your Progress</div>
              <div className="row g-3">
                {stepsData.map((step) => (
                  <div className="col-md-6" key={step.id}>
                    <div
                      className={`info-card bg-white h-100 rounded-4 border border-2 p-3 p-xxl-4 position-relative ${step.id < completedStep
                        ? "border-theme6"
                        : step.id === completedStep
                          ? "border-primary"
                          : "border-white opacity-75 text-muted"
                        }`}>
                      {/* Edit icon for completed cards 1 & 2 — only after headline (full profile) is done */}
                      {step.id < completedStep && step.id <= 2 && isHeadlineVerified && (
                        <button
                          type="button"
                          className="btn btn-sm p-0 position-absolute d-flex align-items-center justify-content-center"
                          style={{
                            top: '12px',
                            right: '12px',
                            width: '35px',
                            height: '35px',
                            borderRadius: '6px',
                            backgroundColor: 'rgba(124, 58, 237, 0.1)',
                            border: 'none',
                            color: '#7c3aed',
                            fontSize: '14px',
                            transition: 'all 0.2s ease',
                            zIndex: 1
                          }}
                          title={`Edit ${step.title}`}
                          onClick={(e) => { e.stopPropagation(); handleEditProfile(step.id === 1 ? 1 : 2); }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#7c3aed';
                            e.currentTarget.style.color = '#fff';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(124, 58, 237, 0.1)';
                            e.currentTarget.style.color = '#7c3aed';
                          }}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                      )}
                      {/* Step number circle */}
                      <div
                        className={`icon bg-opacity-25 d-flex align-items-center justify-content-center fw-semibold rounded-3 ${step.id < completedStep
                          ? "bg-theme6 text-theme6"
                          : step.id === completedStep
                            ? "bg-primary text-primary"
                            : "bg-secondary text-secondary"
                          }`}
                        style={{ width: "30px", height: "30px", marginBottom: "10px" }}>
                        {step.id < completedStep ? "✓" : step.id}
                      </div>

                      <div className="info-box mb-2 pb-1">
                        <div className="fs-16 fw-semibold text-dark mb-1">
                          {step.title}
                          {step.id < completedStep && (
                            <i className="bi bi-check-circle-fill fs-16 text-theme6 ms-2"></i>
                          )}
                        </div>
                        <div className="fs-14 text-muted">{step.description}</div>
                      </div>

                      {/* Status badge */}
                      {step.id < completedStep && (
                        <span className="badge bg-theme6 bg-opacity-10 border border-theme6 rounded-pill text-theme6 py-1">
                          <i className="bi bi-check-circle-fill"></i> Completed
                        </span>
                      )}
                      {step.id === completedStep && (
                        <span className="badge bg-primary bg-opacity-10 border border-primary rounded-pill text-primary py-1">
                          <i className="bi bi-circle-fill"></i> Your Next Step
                        </span>
                      )}
                      {step.id > completedStep && (
                        <span className="badge bg-secondary bg-opacity-10 border rounded-pill text-secondary py-1">
                          🔒 {step.lockedMsg}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {isProfileComplete ? (
              <div className="cta-section rounded-4 d-flex flex-wrap justify-content-between align-items-center gap-3 text-white mt-3 mt-xxl-4 p-3 p-xxl-4">
                <div className="info">
                  <div className="main-heading text-white mb-1">
                    {interviewStatus === "completed"
                      ? "Your profile has been created successfully."
                      : "Your profile has been created successfully."}
                  </div>
                  <div className="fs-6 text-white text-opacity-75 mb-1">
                    {interviewStatus === "completed"
                      ? "We will get back to you soon."
                      : "Now download the Windows app and install it to proceed with your AI-powered interview."}
                  </div>
                  {interviewStatus !== "completed" && (
                    <div className="small text-white text-opacity-50">
                      <i className="bi bi-file-earmark-arrow-down"></i> It is a .exe file of
                      less than 200 MB.
                    </div>
                  )}
                </div>
                {interviewStatus !== "completed" && (
                  <div className="verify-btn-wrapper">
                    <Button
                      variant="theme7"
                      className="btn-custom"
                      onClick={handleDownloadClick}>
                      Go to the download page{" "}
                      <i className="bi bi-arrow-right"></i>
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="cta-section rounded-4 d-flex flex-wrap justify-content-between align-items-center gap-3 text-white mt-3 mt-xxl-4 p-3 p-xxl-4">
                <div className="info">
                  <div className="main-heading text-white mb-1">
                    Complete your profile to download App
                  </div>
                  <div className="fs-6 text-white text-opacity-75 mb-1">
                    To proceed with your online interview, complete your
                    profile.
                  </div>
                  <div className="small text-white text-opacity-50">
                    <i className="bi bi-stopwatch"></i> ~3 minutes to complete
                  </div>
                </div>
                <div className="verify-btn-wrapper">
                  <Button
                    variant="theme7"
                    className="btn-custom"
                    onClick={ProfileVerifyPage}>
                    Complete Your Profile <i className="bi bi-arrow-right"></i>
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div >
      <ToastContainer position="top-right" autoClose={2000} hideProgressBar={false} theme="colored" />

      {/* Reschedule Interview Modal */}
      <Modal show={showRescheduleModal} onHide={() => setShowRescheduleModal(false)} centered size="md">
        <Modal.Body className="p-sm-4">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <div className="fw-bold text-dark" style={{ fontSize: '20px' }}>Reschedule Interview</div>
              <div className="small text-muted">
                Current: {interviewDateTime ? moment(interviewDateTime).format('MMMM DD, YYYY') : '—'}, {selectedCandidate?.interview_time ? (() => { const parts = (selectedCandidate.interview_time || '').split('.')[0].split(':'); const h = parseInt(parts[0], 10); const m = parts[1] || '00'; return `${h === 0 ? 12 : h > 12 ? h - 12 : h}:${m} ${h >= 12 ? 'PM' : 'AM'}`; })() : '—'}
              </div>
            </div>
            <button type="button" className="btn-close" onClick={() => setShowRescheduleModal(false)}></button>
          </div>

          {/* Info Banner */}
          <div className="bg-primary bg-opacity-10 border border-primary border-opacity-25 rounded-3 mb-3 p-3">
            <div className="d-flex align-items-start gap-2">
              <i className="bi bi-info-circle-fill flex-shrink-0 text-primary"></i>
              <div className="small text-dark text-opacity-75">
                {rescheduleWindowText?.days === 0 ? (<div className="mb-2">
                  Rescheduling is only available for today.
                </div>) :
                  (
                    <div className="mb-2">
                      Rescheduling is only available for the next {rescheduleWindowText?.days || 3} days ({moment().format('MMM DD')} - {rescheduleMaxDate ? moment(rescheduleMaxDate).format('MMM DD') : '—'}).
                    </div>)}
                <div>
                  Interviews may be rescheduled only once. Beyond that, any further rescheduling requests must be directed to HR and will be approved at their sole discretion.
                </div>
              </div>
            </div>
          </div>

          {isRescheduleDisabled ? (
            <div className="alert alert-warning d-flex align-items-center gap-2 mb-3 rounded-3" role="alert">
              <i className="bi bi-exclamation-triangle-fill"></i>
              <span>Rescheduling is not possible as the 3-day reschedule window has passed.</span>
            </div>
          ) : (
            <>
              {/* Date Tiles */}
              <div className="select-date-wrapper mb-3">
                <div className="small text-uppercase text-muted fw-semibold mb-2" style={{ letterSpacing: '0.5px' }}>Select New Date</div>
                <div className="d-flex gap-2 flex-wrap">
                  {(() => {
                    const tiles = [];
                    const start = moment(rescheduleMinDate);
                    const end = moment(rescheduleMaxDate);
                    const cursor = start.clone();
                    while (cursor.isSameOrBefore(end, 'day')) {
                      const dateStr = cursor.format('YYYY-MM-DD');
                      const dayAbbr = cursor.format('ddd').toUpperCase();
                      const dateNum = cursor.format('D');
                      const isSelected = rescheduleDate === dateStr;
                      tiles.push(
                        <button
                          key={dateStr}
                          type="button"
                          className="btn d-flex flex-column align-items-center justify-content-center p-2"
                          style={{
                            minWidth: '58px',
                            borderRadius: '12px',
                            border: isSelected ? '2px solid #7c3aed' : '1px solid #d1d5db',
                            backgroundColor: isSelected ? '#f5f3ff' : '#fff',
                            color: isSelected ? '#7c3aed' : '#374151',
                            boxShadow: isSelected ? '0 2px 8px rgba(124,58,237,0.15)' : 'none',
                            transition: 'all 0.15s ease',
                          }}
                          onClick={() => {
                            setRescheduleDate(dateStr);
                            logFieldToSentry('Reschedule date', dateStr);
                          }}
                        >
                          <span className="small fw-medium" style={{ fontSize: '11px', color: isSelected ? '#7c3aed' : '#9ca3af' }}>{dayAbbr}</span>
                          <span className="fw-bold" style={{ fontSize: '18px' }}>{dateNum}</span>
                        </button>
                      );
                      cursor.add(1, 'day');
                    }
                    return tiles;
                  })()}
                </div>
              </div>

              {/* Time Selector */}
              <div className="select-time-wrapper mb-3">
                <div className="small text-uppercase text-muted fw-semibold mb-2" style={{ letterSpacing: '0.5px' }}>Available Time Slots</div>
                <div className="d-flex gap-2">
                  <div className="text-center">
                    <input
                      type="number"
                      onFocus={(e) => e.target.select()}
                      min={1}
                      max={12}
                      value={String(rescheduleHour).padStart(2, '0')}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 0 && val <= 12) setRescheduleHour(val);
                      }}
                      className="form-control text-center fw-bold p-2"
                      style={{ width: '80px', fontSize: '22px', borderRadius: '8px', border: `1px solid ${rescheduleHour === 0 ? '#ef4444' : '#d1d5db'}` }}
                    />
                    <div className="small mt-1" style={{ color: rescheduleHour === 0 ? '#ef4444' : '#9ca3af', fontSize: '10px' }}>HOURS</div>
                    {rescheduleHour === 0 && (
                      <div className="small" style={{ color: '#ef4444', fontSize: '10px' }}>1–12 only</div>
                    )}
                  </div>
                  <span className="fw-bold mt-2" style={{ fontSize: '20px', color: '#374151' }}>:</span>
                  <div className="text-center">
                    <input
                      type="number"
                      min={0}
                      max={59}
                      value={String(rescheduleMinute).padStart(2, '0')}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val >= 0 && val <= 59) setRescheduleMinute(val);
                      }}
                      className="form-control text-center fw-bold p-2"
                      style={{ width: '80px', fontSize: '22px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                    />
                    <div className="small mt-1" style={{ color: '#9ca3af', fontSize: '10px' }}>MINUTES</div>
                  </div>
                  <div className="d-flex flex-column gap-1">
                    <button
                      type="button"
                      className="btn btn-sm fw-semibold lh-sm px-3 py-1"
                      style={{
                        borderRadius: '6px',
                        fontSize: '12px',
                        backgroundColor: rescheduleAmPm === 'AM' ? '#7c3aed' : '#f3f4f6',
                        color: rescheduleAmPm === 'AM' ? '#fff' : '#374151',
                        border: rescheduleAmPm === 'AM' ? '1px solid #7c3aed' : '1px solid #d1d5db',
                      }}
                      onClick={() => {
                        setRescheduleAmPm('AM');
                        logFieldToSentry('Reschedule AM/PM', 'AM');
                      }}
                    >AM</button>
                    <button
                      type="button"
                      className="btn btn-sm fw-semibold lh-sm px-3 py-1"
                      style={{
                        borderRadius: '6px',
                        fontSize: '12px',
                        backgroundColor: rescheduleAmPm === 'PM' ? '#7c3aed' : '#f3f4f6',
                        color: rescheduleAmPm === 'PM' ? '#fff' : '#374151',
                        border: rescheduleAmPm === 'PM' ? '1px solid #7c3aed' : '1px solid #d1d5db',
                      }}
                      onClick={() => {
                        setRescheduleAmPm('PM');
                        logFieldToSentry('Reschedule AM/PM', 'PM');
                      }}
                    >PM</button>
                  </div>
                </div>
              </div>

              {/* Past datetime warning */}
              {rescheduleDate && isSelectedDateTimePast && (
                <div className="alert alert-danger d-flex align-items-center gap-2 mb-3 rounded-3 py-2" role="alert">
                  <i className="bi bi-exclamation-triangle-fill"></i>
                  <span className="small">Selected date and time is in the past. Please choose a future time.</span>
                </div>
              )}

              {/* Acknowledgement Checkbox */}
              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="rescheduleAck"
                  checked={rescheduleAcknowledged}
                  onChange={(e) => setRescheduleAcknowledged(e.target.checked)}
                  disabled={!rescheduleDate}
                  style={{ borderColor: '#7c3aed' }}
                />
                <label className="form-check-label fs-14" htmlFor="rescheduleAck" style={{ color: '#4b5563' }}>
                  I understand that I can only reschedule this interview once.
                </label>
              </div>
            </>
          )}

          {/* Actions */}
          <div className="btn-wrapper d-flex flex-wrap gap-2 mt-4">
            <Button variant='outline-secondary' className="btn-custom" onClick={() => setShowRescheduleModal(false)}>Cancel</Button>
            <Button variant='primary' className="btn-custom"
              disabled={!rescheduleDate || !rescheduleAcknowledged || isRescheduleDisabled || isSelectedDateTimePast || rescheduleHour < 1}
              onClick={handleConfirmReschedule}
            >
              Confirm Reschedule
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <RevalidationPromptModal
        show={showRevalidationModal}
        onHide={() => setShowRevalidationModal(false)}
        setCurrentStep={setCurrentStep}
      />
    </div >
  );
};

export default Account;
